import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { DietLog, reviewDietLog } from "../../services/DietLogService";
import { capitalizeFirstLetter } from "../../utils/commons";

const COMMENT_MAX = 500;

export interface DietLogCardProps {
  log: DietLog;
  /** Called with the server's updated log after a successful review save. */
  onReviewed: (updated: DietLog) => void;
  onRequestDelete: (log: DietLog) => void;
  onOpenPhoto: (url: string) => void;
}

/** Tri-state verdict → chip. `null` = pending. */
const VerdictChip: React.FC<{ followed: boolean | null }> = ({ followed }) => {
  if (followed === true) {
    return <Chip size="small" color="success" label="Followed" />;
  }
  if (followed === false) {
    return <Chip size="small" color="error" label="Not followed" />;
  }
  return <Chip size="small" variant="outlined" label="Pending review" />;
};

/**
 * One diet-log plate: member photo + note, and the coach review controls
 * (tri-state verdict + optional comment). Owns only its own draft review
 * state, so re-rendering it never touches its siblings.
 *
 * Memoized: the parent hands stable callbacks and only replaces the one
 * `log` object that changed, so unchanged cards skip re-rendering when a
 * sibling saves.
 */
const DietLogCard: React.FC<DietLogCardProps> = ({
  log,
  onReviewed,
  onRequestDelete,
  onOpenPhoto,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [followed, setFollowed] = useState<boolean | null>(log.followed);
  const [comment, setComment] = useState<string>(log.coachComment ?? "");
  const [saving, setSaving] = useState(false);

  // Re-sync when the underlying log changes (e.g. after refetch).
  useEffect(() => {
    setFollowed(log.followed);
    setComment(log.coachComment ?? "");
  }, [log.followed, log.coachComment]);

  const dirty =
    followed !== log.followed || comment.trim() !== (log.coachComment ?? "");

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await reviewDietLog(log.id, {
        followed,
        comment: comment.trim() ? comment.trim() : null,
      });
      enqueueSnackbar("Review saved", { variant: "success" });
      onReviewed(updated);
    } catch (error: any) {
      const message =
        error?.response?.data?.data ||
        error?.response?.data?.message ||
        "Failed to save review";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        p: 1.5,
        mb: 1.5,
        backgroundColor: theme.palette.background.paper,
        boxShadow: "0 1px 6px rgba(0,0,0,0.4)",
      }}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        mb={1}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              size="small"
              variant="outlined"
              label={capitalizeFirstLetter(log.category.toLowerCase())}
            />
            <VerdictChip followed={followed} />
          </Box>
          <Typography variant="caption" color="text.secondary" mt={0.5}>
            Logged {dayjs(log.loggedAt).format("MMM D, YYYY · h:mm A")}
          </Typography>
        </Box>
        <IconButton
          size="small"
          color="error"
          onClick={() => onRequestDelete(log)}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        component="img"
        src={log.photoUrl}
        alt={`${log.category} plate`}
        loading="lazy"
        onClick={() => onOpenPhoto(log.photoUrl)}
        sx={{
          width: "100%",
          maxHeight: 160,
          objectFit: "cover",
          borderRadius: 2,
          cursor: "zoom-in",
          display: "block",
          backgroundColor: theme.palette.background.default,
        }}
      />

      {log.note && (
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary">
            Member note
          </Typography>
          <Typography variant="body2">{log.note}</Typography>
        </Box>
      )}

      {/* Coach verdict */}
      <Box display="flex" gap={1} mt={1.5}>
        <Button
          fullWidth
          size="small"
          color="success"
          variant={followed === true ? "contained" : "outlined"}
          startIcon={
            followed === true ? <ThumbUpAltIcon /> : <ThumbUpAltOutlinedIcon />
          }
          onClick={() => setFollowed(followed === true ? null : true)}
          sx={{ borderRadius: 2 }}
        >
          Followed
        </Button>
        <Button
          fullWidth
          size="small"
          color="error"
          variant={followed === false ? "contained" : "outlined"}
          startIcon={
            followed === false ? (
              <ThumbDownAltIcon />
            ) : (
              <ThumbDownAltOutlinedIcon />
            )
          }
          onClick={() => setFollowed(followed === false ? null : false)}
          sx={{ borderRadius: 2 }}
        >
          Not followed
        </Button>
      </Box>

      <TextField
        fullWidth
        multiline
        minRows={1}
        size="small"
        placeholder={
          followed === false
            ? "Add a comment for the member (recommended)"
            : "Add a comment (optional)"
        }
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        inputProps={{ maxLength: COMMENT_MAX }}
        helperText={`${comment.length}/${COMMENT_MAX}`}
        FormHelperTextProps={{ sx: { textAlign: "right", m: 0 } }}
        sx={{ mt: 1 }}
      />

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={1}
      >
        <Typography variant="caption" color="text.secondary">
          {log.reviewedAt
            ? `Reviewed ${dayjs(log.reviewedAt).format("MMM D, YYYY · h:mm A")}`
            : "Not reviewed yet"}
        </Typography>
        <Button
          size="small"
          color="secondary"
          variant="contained"
          disabled={!dirty || saving}
          onClick={handleSave}
          sx={{ borderRadius: 2 }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

export default React.memo(DietLogCard);
